/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    Output,
    Type,
} from '@angular/core';

import {
    Observable,
    Subscription,
} from 'rxjs';

import { AviFaultAlertModalComponent } from 'ng/modules/core/components';
import { DialogService } from 'dialog-service';
import { EVENTS_EVENTS_LIST } from 'ng/modules/core/constants/state-names.constants';
import { IFaultItem } from 'ng/modules/collections/factories';
import { L10nService } from '@vmw/ngx-vip';
import { StateService } from '@uirouter/core';

import './avi-app-level-alert.component.less';
import * as l10n from './avi-app-level-alert.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Enum for possible status values of the alert bar.
 */
export enum Status {
    NEUTRAL = 'neutral',
    INFO = 'info',
    SUCCESS = 'success',
    WARNING = 'warning',
    DANGER = 'danger',
    ALT = 'alt',
    LOADING = 'loading',
}

const FAULT_LEVEL_DIALOG_ID = 'avi-fault-level-dialog';

/**
 * For compare with type to display action buttons.
 */
const SYSTEM_FAULTS_LIMITS = 'system_limits_faults';

/**
 * @description
 *  App level alert component which makes use of cds-alert.
 *
 * @example
 *  <avi-app-level-alert
 *      messages="['message1', 'message2']"
 *      showAction="true"
 *      actionLabel="Activate"
 *      (onClose)="onAlertClose()"
 *  ></avi-app-level-alert>
 *
 * @author Nisar Nadaf, Umesh Chovatiya
 */
@Component({
    selector: 'avi-app-level-alert',
    templateUrl: './avi-app-level-alert.component.html',
})
export class AviAppLevelAlertComponent implements AfterViewInit {
    /**
     * Status of the alert bar.
     */
    @Input()
    public status: Status = Status.INFO;

    /**
     * List of messages to be shown.
     */
    @Input()
    public messages? : IFaultItem[];

    /**
     * Message that can be updated dynamically.
     */
    @Input()
    public dynamicMessages$? : Observable<string>;

    /**
     * Label for action button.
     */
    @Input()
    public actionLabel ?: string;

    /**
     * Flag to allow user to close/dismiss alert. False to hide close icon.
     */
    @Input()
    public closable ? = true;

    /**
     * Called when the user has clicked on action button.
     */
    @Output()
    public onClick = new EventEmitter<void>();

    /**
     * Called when the user has clicked on close button of alert.
     * Emit the message that was closed.
     */
    @Output()
    public onClose = new EventEmitter<IFaultItem | void>();

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Return string for system fault type to display actions buttons.
     */
    public readonly systemFaultType = SYSTEM_FAULTS_LIMITS;

    /**
     * Subscription to the dynamic meesage to be shown.
     */
    private subscription: Subscription;

    public constructor(
        l10nService: L10nService,
        private readonly $state: StateService,
        private readonly dialogService: DialogService,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.actionLabel = l10nService.getMessage(l10nKeys.defaultActionBtnLabel);
    }

    /** @override */
    public ngAfterViewInit(): void {
        if (this.dynamicMessages$) {
            this.subscription = this.dynamicMessages$.subscribe(data => {
                this.messages = [{ message: data }];
            });
        }
    }

    /**
     * trackBy Index Method.
     */
    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Returns true if a onClick handler has been passed in, so we display Action button on alert.
     */
    public get hasActionButton(): boolean {
        return Boolean(this.onClick.observers.length);
    }

    /**
     * Handler for close button click. Emits onClose event with the message that was closed.
     */
    public closeAlert(message?: IFaultItem): void {
        this.onClose.emit(message);
    }

    /**
     * Handler for Action button click. Emits onClick event.
     */
    public handleAction(): void {
        this.onClick.emit();
    }

    /**
     * Handle to open dialog if faults are available.
     */
    public handleViewDetails(details: string[]): void {
        this.dialogService.add({
            id: FAULT_LEVEL_DIALOG_ID,
            component: AviFaultAlertModalComponent as Type<Component>,
            componentProps: {
                details,
                onClose: () => this.dialogService.remove(FAULT_LEVEL_DIALOG_ID),
            },
        });
    }

    /**
     * Navigate to Events list page.
     */
    public redirectToEventsPage(): void {
        const params: Record<string, string> = {
            timeframe: '6h',
            inclSystemEvents: 'true',
            search: 'event_id=SYSTEM_LIMIT_BEYOND_SUPPORTED_CONFIG',
        };

        this.$state.go(EVENTS_EVENTS_LIST, params);
    }

    /** @override */
    public ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }
}
